import { TechMetadataFieldValue } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { isNullOrUndefinedOrEmpty } from '@ibm-aspera/connect-sdk-js/dist/esm/utils';
import { Badge, Button, Grid } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { Modal, PolarisIcon } from '@tmt-insights/oms_fe_core/columns';
import { CompareSection } from './BBCFileIngestMediaInfo';

type MediaInfoCompareModalProps = {
  fileName: string;
  fileType: string;
  open: boolean;
  onClose: () => void;
  mismatchedFields: string[];
  uniqueFields: CompareSection[];
};

const getScopedFieldKey = (sectionIndex: number, fieldKey: string) => `${sectionIndex}::${fieldKey}`;

const renderFieldNameRow = (
  value: TechMetadataFieldValue | undefined,
  overlayClassName?: string,
  opts: { showConnector?: boolean; highlight?: boolean; validated: boolean } = { validated: true }
) => {
  const { showConnector = false, highlight = false, validated } = opts;
  return (
    <CellValue validated={validated} className="cell-value">
      {overlayClassName ? <HoverPanel className={`hover-panel ${overlayClassName}`} aria-hidden="true" /> : null}
      <CellText>
        {value ?? '-'}
        {highlight && <PolarisIcon icon="error_circle" width={20} height={20} />}
      </CellText>
      {showConnector ? <HoverConnector className="hover-connector" aria-hidden="true" /> : null}
    </CellValue>
  );
};

const renderRowValue = (
  value: TechMetadataFieldValue | undefined,
  overlayClassName?: string,
  opts: { showConnector?: boolean; highlight?: boolean } = {}
) => {
  const { showConnector = false, highlight = false } = opts;
  return (
    <CellValue validated className="cell-value">
      {overlayClassName ? <HoverPanel className={`hover-panel ${overlayClassName}`} aria-hidden="true" /> : null}
      <CellText>
        {isNullOrUndefinedOrEmpty(value) ? '-' : String(value)}
        {highlight && <PolarisIcon icon="error_circle" width={20} height={20} />}
      </CellText>
      {showConnector ? <HoverConnector className="hover-connector" aria-hidden="true" /> : null}
    </CellValue>
  );
};

const MediaInfoCompareModal = ({ open, fileName, fileType, onClose, mismatchedFields, uniqueFields }: MediaInfoCompareModalProps) => {
  const [filterMismatches, setFilterMismatches] = React.useState(false);

  const indexedSections = useMemo(() => uniqueFields.map((section, sectionIndex) => ({ ...section, sectionIndex })), [uniqueFields]);

  const filteredSections = useMemo(() => {
    if (!filterMismatches) return indexedSections;
    return indexedSections
      .map((section) => ({
        ...section,
        fields: section.fields.filter((f) => mismatchedFields.includes(getScopedFieldKey(section.sectionIndex, f.field)))
      }))
      .filter((section) => section.fields.length > 0);
  }, [filterMismatches, indexedSections, mismatchedFields]);

  useEffect(() => {
    if (!open) {
      setFilterMismatches(false);
    }
  }, [open]);

  const title = (
    <div>
      <Title>Compare Info</Title>
      <Filename>{fileName}</Filename>
    </div>
  );

  return (
    <StyledModal open={open} icon={<PolarisIcon icon="compare_arrows" />} title={title} onClose={onClose}>
      <Grid container spacing={2} direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
        <ButtonMismatches
          disabled={!mismatchedFields.length}
          onClick={() => setFilterMismatches(!filterMismatches)}
          variant="outlined"
          color="primary"
          endIcon={mismatchedFields.length > 0 ? <StyledBadge badgeContent={mismatchedFields.length} color="warning" /> : null}
        >
          Mismatches Only
        </ButtonMismatches>
      </Grid>
      <OuterContentsWrapper>
        <ContentsWrapper>
          <table>
            <colgroup>
              <col />
              <col />
              <col />
            </colgroup>
            <thead>
              <tr>
                <TableHeader />
                <TableHeader>Media Info</TableHeader>
                <TableHeader>{fileType} Specifications</TableHeader>
              </tr>
            </thead>
            <tbody>
              {filteredSections.map(({ sectionName, isExtra, fields, sectionIndex }) => (
                <React.Fragment key={`${sectionName}-${sectionIndex}`}>
                  <tr className={`section-header-row${isExtra ? ' section-header-extra' : ''}`}>
                    <td colSpan={3}>
                      <SectionLabel>
                        {sectionName}
                        {isExtra && <PolarisIcon icon="error_circle" width={14} height={14} />}
                      </SectionLabel>
                    </td>
                  </tr>
                  {fields.map(({ field, label, actual, expected, validated }, index) => {
                    const isFirstInvalid = !validated && (index === 0 || fields[index - 1].validated);
                    return (
                      <tr key={field} className={isFirstInvalid ? 'section-start' : ''}>
                        <td className="field-cell">{renderFieldNameRow(label, undefined, { validated })}</td>
                        <td className="media-cell">
                          {renderRowValue(actual, 'left-panel', {
                            showConnector: true,
                            highlight: mismatchedFields.includes(getScopedFieldKey(sectionIndex, field))
                          })}
                        </td>
                        <td className="spec-cell">{renderRowValue(expected, 'spec-panel')}</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </ContentsWrapper>
      </OuterContentsWrapper>
    </StyledModal>
  );
};

export default MediaInfoCompareModal;

const StyledModal = styled(Modal)`
  && {
    .modal-body {
      display: flex;
      flex-direction: column;
    }

    div:nth-of-type(3) {
      max-width: 1366px;
    }

    .modal-title-section {
      span {
        background: none;

        svg {
          color: ${(props) => props.theme.palette.tertiary.contrastText};
        }
      }
    }

    .MuiGrid-root {
      border: 0;
    }
  }
`;

const Title = styled.div`
  font-size: 1.357rem;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.19px;
`;

const Filename = styled.div`
  color: ${(props) => props.theme.palette.default.light};
  font-size: 1.14rem;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
`;

const OuterContentsWrapper = styled.div`
  background: ${({ theme }) => theme.palette.background.default};
  padding: ${({ theme }) => theme.spacing(2)};
`;

const ContentsWrapper = styled.div`
  background: ${({ theme }) => theme.palette.background.paper};

  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    background: ${(props) => props.theme.palette.background.paper};
  }

  col:nth-of-type(1) {
    width: 24%;
  }

  col:nth-of-type(2) {
    width: 36%;
  }

  col:nth-of-type(3) {
    width: 40%;
  }

  th,
  td {
    padding: ${({ theme }) => theme.spacing(1.5, 2)};
    text-align: left;
    display: table-cell;
  }

  thead th {
    font-weight: 600;

    &:nth-of-type(1) {
      text-align: center;
    }
  }

  tbody td {
    border-top: 1px solid ${({ theme }) => theme.palette.background.default};
    padding: 0;
    position: relative;
    overflow: visible;

    &:nth-of-type(2) {
      border-right: 3px solid ${({ theme }) => theme.palette.background.default};
      background: ${(props) => (props.theme.palette.mode === 'dark' ? props.theme.palette.default.dark : props.theme.palette.default.light)};
    }

    &:nth-of-type(3) {
      background: ${(props) => (props.theme.palette.mode === 'dark' ? props.theme.palette.default.dark : props.theme.palette.default.light)};
    }
  }

  tbody tr.section-start td {
    border-top-width: 2px;
    border-top-style: solid;
    border-top-color: ${(props) => props.theme.palette.default.main};
  }

  tbody tr {
    position: relative;
  }

  tbody tr:hover {
    z-index: 2;
  }

  tbody tr:hover td {
    z-index: 2;
  }

  tbody tr:hover .media-cell {
    z-index: 4;
  }

  tbody tr:hover .field-cell {
    z-index: 5;
  }

  tbody tr:hover .spec-cell {
    z-index: 3;
  }

  tbody tr:hover .hover-panel,
  tbody tr:hover .hover-indicator,
  tbody tr:hover .hover-connector {
    opacity: 1;
  }

  tbody tr:hover .hover-panel {
    transform: scale(1);
  }

  tbody tr:hover .hover-indicator {
    transform: translateY(-50%) scale(1);
  }

  tbody tr:hover .hover-connector {
    transform: translateY(-50%) scaleX(1);
  }

  tbody tr.section-header-row td {
    background: ${({ theme }) => theme.palette.background.default};
    padding: 0;
    border-top: none;
  }

  tbody tr.section-header-extra td {
    background: ${({ theme }) => `${theme.palette.warning.main}18`};
  }

  tbody tr.section-header-row + tr td {
    border-top: none;
  }
`;

const TableHeader = styled.th`
  text-transform: capitalize;
`;

const SectionLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.75)};
  padding: ${({ theme }) => theme.spacing(1, 2)};
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.5;

  svg {
    color: ${({ theme }) => theme.palette.warning.main};
    opacity: 1;
  }
`;

const CellValue = styled.div<{ validated?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  min-height: 42px;
  font-style: normal;
  font-weight: 400;
  padding: ${({ theme }) => theme.spacing(1.5, 2)};
  ${(props) =>
    !props.validated
      ? `
    opacity: 0.5;
  `
      : null};
`;

const CellText = styled.span`
  display: block;
  width: 100%;
  position: relative;
  z-index: 2;

  svg {
    color: ${({ theme }) => theme.palette.warning.main};
    opacity: 1;
    flex-shrink: 0;
    margin-left: auto;
    position: absolute;
    right: 0;
  }
`;

const HoverPanel = styled.span`
  position: absolute;
  top: -8px;
  bottom: -8px;
  opacity: 0;
  pointer-events: none;
  background: rgba(72, 92, 138, 0.82);
  border: 1px solid rgba(96, 167, 255, 0.82);
  box-shadow: inset 0 0 0 1px rgba(96, 167, 255, 0.18);
  transform: scale(0.985);
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
  z-index: 1;

  &.left-panel {
    left: calc(-66.6667% - 6px);
    right: 10px;
    border-radius: 8px;
  }

  &.spec-panel {
    left: 8px;
    right: -13px;
    border-radius: 8px;
  }
`;

const HoverConnector = styled.span`
  position: absolute;
  top: 50%;
  right: -12px;
  width: 22px;
  height: 2px;
  background: rgba(96, 167, 255, 0.85);
  opacity: 0;
  transform: translateY(-50%) scaleX(0.4);
  transform-origin: right center;
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
  pointer-events: none;
  z-index: 2;

  &[aria-hidden='true'] {
    display: block;
  }
`;

const ButtonMismatches = styled(Button)`
  color: ${(props) => props.theme.palette.tertiary.contrastText};
  border-color: ${(props) => props.theme.palette.tertiary.contrastText};
`;

const StyledBadge = styled(Badge)`
  margin-right: ${(props) => props.theme.spacing(1)};
  margin-left: ${(props) => props.theme.spacing(1)};
`;
